import React, { useState, useEffect } from 'react';

const BRAND   = '#5A38FD';
const BRAND_L = '#a78bfa';
const SANS    = '"Geist", "Inter", -apple-system, sans-serif';

// ─── Premium auth overlay (sign-in / data load) ───────────────────────────
const AuthLoadingOverlay = ({ message, isVisible, onComplete, minDuration = 0 }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    if (!isVisible) {
      setFadeOut(true);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDuration - elapsed);
      const timer = setTimeout(() => {
        setShouldRender(false);
        if (onComplete) onComplete();
      }, 550 + remaining);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete, startTime, minDuration]);

  if (!shouldRender) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.96)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
      opacity: fadeOut ? 0 : 1,
      transition: 'opacity 0.5s ease',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }}>
      {/* Ambient glow */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 320, height: 320,
        background: `radial-gradient(ellipse at center, rgba(90,56,253,0.14) 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, position: 'relative' }}>
        {/* Logo with glow rings */}
        <div style={{ position: 'relative', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 96, height: 96,
            background: `radial-gradient(ellipse at center, rgba(90,56,253,0.45) 0%, transparent 70%)`,
            borderRadius: '50%',
            animation: 'nvAuthGlow 2.4s ease-in-out infinite',
          }} />
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 72, height: 72,
            border: `1px solid rgba(167,139,250,0.3)`,
            borderRadius: '50%',
            animation: 'nvAuthRing 2.4s ease-in-out infinite',
          }} />
          <img
            src="/nilvision-logo-gradient-background.png"
            alt="NILVISION"
            style={{ width: 44, height: 44, objectFit: 'contain', position: 'relative', zIndex: 1, display: 'block' }}
            onError={(e) => { e.target.src = '/nilvision-logo-gradient-backdrop.png'; }}
          />
        </div>

        {/* Wordmark */}
        <div style={{
          fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.22em',
          fontFamily: SANS,
          background: 'linear-gradient(135deg, #ffffff 0%, #c4b5fd 55%, #93c5fd 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>
          NILVISION
        </div>

        {/* Animated dots */}
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 5, height: 5, borderRadius: '50%',
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND_L})`,
              animation: `nvAuthDot 1.5s ease-in-out ${i * 0.22}s infinite`,
            }} />
          ))}
        </div>

        {/* Message */}
        {message && (
          <p style={{
            margin: 0,
            fontSize: '0.78rem',
            color: 'rgba(167,139,250,0.55)',
            fontFamily: SANS,
            letterSpacing: '0.04em',
          }}>
            {message}
          </p>
        )}
      </div>

      <style>{`
        @keyframes nvAuthGlow {
          0%, 100% { opacity: 0.4; transform: translate(-50%,-50%) scale(1); }
          50%       { opacity: 1;   transform: translate(-50%,-50%) scale(1.22); }
        }
        @keyframes nvAuthRing {
          0%, 100% { opacity: 0.25; transform: translate(-50%,-50%) scale(1); }
          50%       { opacity: 0.65; transform: translate(-50%,-50%) scale(1.1); }
        }
        @keyframes nvAuthDot {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.35; }
          40%            { transform: scale(1.4); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ─── Full splash screen (initial site load only) ──────────────────────────
const SplashScreen = ({ isVisible, onComplete, minDuration = 3000 }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const [logoVisible, setLogoVisible] = useState(false);
  const [wordmarkVisible, setWordmarkVisible] = useState(false);
  const [taglineVisible, setTaglineVisible] = useState(false);
  const [progressVisible, setProgressVisible] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    const t0 = setTimeout(() => setLogoVisible(true), 150);
    const t1 = setTimeout(() => setWordmarkVisible(true), 550);
    const t2 = setTimeout(() => setTaglineVisible(true), 950);
    const t3 = setTimeout(() => setProgressVisible(true), 1200);
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setFadeOut(true);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDuration - elapsed);
      const timer = setTimeout(() => {
        setShouldRender(false);
        if (onComplete) onComplete();
      }, 1400 + remaining);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete, startTime, minDuration]);

  if (!shouldRender) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#000000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
      opacity: fadeOut ? 0 : 1,
      transition: 'opacity 1.4s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      pointerEvents: fadeOut ? 'none' : 'all',
    }}>

      {/* Ambient radial glow */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse 55% 45% at 50% 50%, rgba(90, 56, 253, 0.18) 0%, rgba(90, 56, 253, 0.04) 45%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Subtle grid overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(rgba(90, 56, 253, 0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(90, 56, 253, 0.04) 1px, transparent 1px)
        `,
        backgroundSize: '64px 64px',
        pointerEvents: 'none',
      }} />

      {/* Main content */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        position: 'relative', userSelect: 'none',
      }}>

        {/* Logo with glow ring */}
        <div style={{
          position: 'relative', marginBottom: '32px',
          opacity: logoVisible ? 1 : 0,
          transform: logoVisible ? 'scale(1) translateY(0)' : 'scale(0.82) translateY(12px)',
          transition: 'opacity 0.75s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.75s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}>
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '160px', height: '160px',
            background: 'radial-gradient(ellipse at center, rgba(90, 56, 253, 0.4) 0%, rgba(90, 56, 253, 0.08) 50%, transparent 70%)',
            borderRadius: '50%',
            animation: 'nvGlowPulse 3s ease-in-out infinite',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px', height: '120px',
            border: '1px solid rgba(167, 139, 250, 0.22)',
            borderRadius: '50%',
            animation: 'nvRingExpand 3s ease-in-out infinite',
            pointerEvents: 'none',
          }} />
          <img
            src="/nilvision-logo-gradient-background.png"
            alt="NILVISION"
            style={{ width: '88px', height: '88px', objectFit: 'contain', position: 'relative', zIndex: 1, display: 'block' }}
            onError={(e) => { e.target.src = '/nilvision-logo-gradient-backdrop.png'; }}
          />
        </div>

        {/* NILVISION wordmark */}
        <div style={{
          marginBottom: '10px',
          opacity: wordmarkVisible ? 1 : 0,
          transform: wordmarkVisible ? 'translateY(0)' : 'translateY(14px)',
          transition: 'opacity 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}>
          <h1 style={{
            margin: 0, fontSize: '26px', fontWeight: '800',
            fontFamily: SANS,
            letterSpacing: wordmarkVisible ? '0.28em' : '0.06em',
            transition: 'letter-spacing 1.1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            background: 'linear-gradient(135deg, #ffffff 0%, #c4b5fd 55%, #93c5fd 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            NILVISION
          </h1>
        </div>

        {/* Divider */}
        <div style={{
          width: '48px', height: '1px',
          background: `linear-gradient(90deg, transparent, rgba(167, 139, 250, 0.65), transparent)`,
          marginBottom: '12px',
          opacity: taglineVisible ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }} />

        {/* Tagline */}
        <div style={{
          marginBottom: '52px',
          opacity: taglineVisible ? 1 : 0,
          transform: taglineVisible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}>
          <p style={{
            margin: 0, fontSize: '11px', fontWeight: '600',
            fontFamily: SANS,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'rgba(167, 139, 250, 0.7)',
          }}>
            Your AI NIL Agent
          </p>
        </div>

        {/* Progress bar */}
        <div style={{ width: '100px', opacity: progressVisible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
          <div style={{
            height: '2px', background: 'rgba(90, 56, 253, 0.18)',
            borderRadius: '2px', overflow: 'hidden', position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 0, bottom: 0, width: '50%',
              background: `linear-gradient(90deg, transparent, ${BRAND}, ${BRAND_L}, transparent)`,
              borderRadius: '2px',
              animation: 'nvProgressScan 1.8s ease-in-out infinite',
            }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes nvGlowPulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50%       { opacity: 1;   transform: translate(-50%, -50%) scale(1.18); }
        }
        @keyframes nvRingExpand {
          0%, 100% { opacity: 0.35; transform: translate(-50%, -50%) scale(1); }
          50%       { opacity: 0.75; transform: translate(-50%, -50%) scale(1.12); }
        }
        @keyframes nvProgressScan {
          0%   { left: -55%; }
          100% { left: 110%; }
        }
      `}</style>
    </div>
  );
};

// ─── Public export: picks the right variant ───────────────────────────────
const LoadingScreen = ({ variant = 'splash', message, onComplete, isVisible = true, minDuration }) => {
  if (variant === 'auth') {
    return (
      <AuthLoadingOverlay
        message={message}
        isVisible={isVisible}
        onComplete={onComplete}
        minDuration={minDuration ?? 0}
      />
    );
  }
  return (
    <SplashScreen
      isVisible={isVisible}
      onComplete={onComplete}
      minDuration={minDuration ?? 3000}
    />
  );
};

export default LoadingScreen;
